<template>
  <div class="p-4">
    <div class="row mb-3">
      <!-- Confirm Modal -->
      <confirm-modal
        key="confirmStatusModal"
        v-if="showStatusConfirmModal"
        @noEvent="showStatusConfirmModal = false"
        :yesEvent="onApproveStatusConfirmed"
        text="Are you sure you want to change the status of this attribution study?"
      >
      </confirm-modal>

      <!-- Edit Modal -->
      <attribution-study-edit-modal
        key="editModal"
        v-if="showEditModal"
        @noEvent="showEditModal = false"
        :yesEvent="handleEditYesEvent"
        :study="selectedAttributionStudy"
        :cuts="cuts"
        :cutOptions="cutOptions"
      >
      </attribution-study-edit-modal>

      <!-- Campaign Retargeting Modal -->
      <campaign-retargeting-modal
        key="campaignRetargetingModal"
        v-if="showCampaignRetargetingModal"
        @noEvent="showCampaignRetargetingModal = false"
        :yesEvent="handleCampaignRetargetingSuccess"
      >
      </campaign-retargeting-modal>

      <!-- Run Midpoint Study Modal -->
      <run-midpoint-study-modal
        key="runMidpointStudyModal"
        v-if="showRunMidpointStudyModal"
        @noEvent="showRunMidpointStudyModal = false"
        :yesEvent="handleRunMidpointStudy"
        :study="selectedStudyForMidpoint"
      >
      </run-midpoint-study-modal>

      <!-- Attribution Studies List -->
      <div class="mb-3 d-flex justify-content-between">
        <div class="d-flex align-items-center">
          <h2>Attribution Studies</h2>
        </div>
        <div class="d-flex align-items-center gap-2">
          <button class="btn btn-secondary" @click="openCampaignRetargetingModal">
            <font-awesome-icon :icon="['fas', 'bullseye']" class="me-1" />
            Campaign Retargeting
          </button>
          <button class="btn btn-primary" @click="loadStudies">
            Refresh Studies
          </button>
        </div>
      </div>

      <label for="filter-text-box" class="col-sm-2 col-form-label font-md"
        >Quick Filter</label
      >
      <div class="col-sm-10">
        <input
          type="text"
          id="filter-text-box"
          placeholder="Filter..."
          v-model="quickFilter"
          class="form-control"
        />
        <div class="text-muted mt-1">
          Showing all {{ filteredStudies.length }} studies
          <template v-if="quickFilter">
            (filtered from {{ studies.length }} total)
          </template>
        </div>
      </div>

      <!-- Loop through studies and display as cards -->
      <div class="row g-4">
        <div
          class="col-md-4"
          v-for="study in filteredStudies"
          :key="study.attributionId"
        >
          <div class="card shadow border-0" style="height: 27rem; box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;">
            <div class="card-body d-flex flex-column">
              <div class="d-flex justify-content-between align-items-start mb-3">
                <h5 class="card-title text-primary text-truncate m-0" :title="study.studyName">{{ study.studyName }}</h5>
                <div class="d-flex gap-2">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary"
                    @click="logStudy(study)"
                  >
                    <font-awesome-icon :icon="['fas', 'circle-info']" />
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary"
                    @click="openEditModal(study)"
                  >
                    <font-awesome-icon :icon="['fas', 'pen']" />
                  </button>
                </div>
              </div>

              <div class="mb-3">
                <div class="d-flex justify-content-between mb-2">
                  <span class="text-muted">ID:</span>
                  <span>{{ study.attributionId }}</span>
                </div>

                <div class="d-flex justify-content-between mb-2">
                  <span class="text-muted">Customer:</span>
                  <span>{{ study.customerName }}</span>
                </div>

                <div class="d-flex justify-content-between mb-2">
                  <span class="text-muted">Objective:</span>
                  <span>{{ study.objective }}</span>
                </div>

                <div class="d-flex justify-content-between mb-2">
                  <span class="text-muted">Type:</span>
                  <span>{{ study.studyType }}</span>
                </div>

                <div class="d-flex justify-content-between mb-2">
                  <span class="text-muted">Status:</span>
                  <span class="badge bg-secondary">{{ study.status }}</span>
                </div>

                <div class="d-flex justify-content-between mb-2">
                  <span :class="{ 'text-muted': study.campaigns && study.campaigns.length > 0, 'text-danger': !study.campaigns || study.campaigns.length === 0 }">Campaign(s):</span>
                  <span :class="{ 'text-success': study.campaigns && study.campaigns.length > 0, 'text-danger': !study.campaigns || study.campaigns.length === 0 }">
                    <template v-if="study.campaigns && study.campaigns.length > 0">
                      <template v-for="(campaignId, index) in getUniqueCampaignIds(study.campaigns)" :key="campaignId">
                        <span>{{ campaignId }}</span><span v-if="index < getUniqueCampaignIds(study.campaigns).length - 1">, </span>
                      </template>
                    </template>
                    <span v-else>Not Assigned</span>
                  </span>
                </div>
                
                <div v-if="study.objective === 'Website Visitation Analysis'" class="d-flex justify-content-between mb-2">
                  <span :class="{ 'text-muted': study.pixelCount, 'text-danger': !study.pixelCount }">Pixels:</span>
                  <span :class="{ 'text-success': study.pixelCount, 'text-danger': !study.pixelCount }">{{ study.pixelCount || 0 }}</span>
                </div>
              </div>

              <div class="small text-muted mb-3">
                <div class="mb-1">
                  {{ this.formatDate(study.startDate) }} - {{ this.formatDate(study.endDate) }}
                </div>
                <div class="mb-1">
                  Post Campaign: {{ this.formatDate(study.postStudyEndDate) }}
                </div>
                <div>
                  {{ this.getDaysRemainingLabel(study) }}
                </div>
              </div>

              <div class="d-flex flex-wrap gap-2">
                <button
                  v-if="isReadyToRun(study) && !isEligibleForMidpointStudy(study)"
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  @click="onRunStudyClick(study)"
                  :disabled="study.objective === 'Website Visitation Analysis' && (!study.pixelCount || Number(study.pixelCount) === 0)"
                >
                  <font-awesome-icon :icon="['fas', 'arrows-spin']" class="me-1" />
                  Run Study
                </button>

                <button
                  v-if="isReadyToRun(study) && isEligibleForMidpointStudy(study)"
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  @click="onRunMidpointStudyClick(study)"
                  :disabled="study.objective === 'Website Visitation Analysis' && (!study.pixelCount || Number(study.pixelCount) === 0)"
                >
                  <font-awesome-icon :icon="['fas', 'arrows-spin']" class="me-1" />
                  Run Midpoint Study
                </button>

                <button
                  v-if="isReviewAvailable(study)"
                  type="button"
                  class="btn btn-sm btn-outline-info"
                  @click="openStudyLink(study)"
                >
                  Review
                </button>

                <button
                  v-if="isCompleteAvailableByStatus(study)"
                  type="button"
                  class="btn btn-sm btn-outline-success"
                  @click="selectStatus('COMPLETE', study)"
                >
                  <font-awesome-icon :icon="['fas', 'check']" class="me-1" />
                  Complete
                </button>

                <button
                  v-if="isMidCompleteAvailableByStatus(study)"
                  type="button"
                  class="btn btn-sm btn-outline-success"
                  @click="selectStatus('MID_STUDY_COMPLETE', study)"
                >
                  <font-awesome-icon :icon="['fas', 'check']" class="me-1" />
                  Mid Complete
                </button>

                <button
                  v-if="isResetToPendingAvailable(study)"
                  type="button"
                  class="btn btn-sm btn-outline-warning"
                  @click="selectStatus('PENDING', study)"
                >
                  <font-awesome-icon :icon="['fas', 'rotate-left']" class="me-1" />
                  Reset to Pending
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import AttributionService from "@/services/AttributionService";
import ConfirmModal from "@/components/ConfirmModal";
import CheckIcon from "@/components/CheckIcon";
import * as dayjs from "dayjs";
import DeviceService from "@/services/DeviceService";
import AppUtil from "@/utils/AppUtil";
import CancelIcon from "@/components/CancelIcon";
import StepProgress from "@/components/StepProgress";
import AttributionStudyEditModal from "@/components/AttributionStudyEditModal";
import { toRaw } from 'vue';
import _ from 'lodash';
import CampaignRetargetingModal from "@/components/CampaignRetargetingModal";
import RunMidpointStudyModal from "@/components/RunMidpointStudyModal";

export default {
  name: "Attribution",
  components: {
    CancelIcon,
    CheckIcon,
    ConfirmModal,
    StepProgress,
    AttributionStudyEditModal,
    CampaignRetargetingModal,
    RunMidpointStudyModal,
  },
  props: ["prop1", "prop2"],
  data() {
    return {
      quickFilter: "",
      selectedStatus: "PENDING",
      currentStudyStep: 0,
      model: streetmetrics.model,
      service: new AttributionService(),
      filteredStudies: [],
      studies: [],
      showConfirmModal: false,
      showEditModal: false,
      onApproveStatusConfirmed: function () {},
      showLocationModalModal: false,
      currentAttributionStudy: null,
      showStatusConfirmModal: false,
      pixels: [],
      selectedAttributionStudy: null,
      attributionStudyStatus: {
        PENDING: "PENDING",
        PROCESSING: "PROCESSING",
        ATTENTION_REQUIRED: "ATTENTION_REQUIRED",
        ACTIVE: "ACTIVE",
        UNDER_REVIEW: "UNDER_REVIEW",
        COMPLETE: "COMPLETE",
        MID_STUDY_COMPLETE: "MID_STUDY_COMPLETE",
      },
      selectedStudyDetails: [],
      selectedStudy: null,
      editMode: false,
      cuts: [],
      showCampaignRetargetingModal: false,
      showRunMidpointStudyModal: false,
      selectedStudyForMidpoint: null,
    };
  },
  computed: {
    filteredStudies() {
      if (!this.studies || !this.quickFilter) return this.studies || [];
      // Allow user to search by multiple terms separated by spaces
      const searchTerms = this.quickFilter.toLowerCase().split(' ');
      // Filter studies based on whether each study contains all search terms
      return _.filter(this.studies, (study) => {
        const studyValues = _.values(study).join(' ').toLowerCase();
        return _.every(searchTerms, (term) => studyValues.includes(term));
      });
    },
  },
  mounted() {
    this.loadStudies();
  },
  methods: {
    logStudy(study) {
      console.log('Attribution Study:', toRaw(study));
      this.model.toast.background = "bg-info";
      this.model.toast.message = "Study logged to console";
    },
    openStudyLink(attributionStudy) {
      let url = `https://nova.streetmetrics.com/attribution/${attributionStudy.attributionId}`;
      window.open(url, "_blank");
    },
    handleEditYesEvent(study, deliveryDate, notes, cuts, retargetingCsvColumns) {
      const studyPayload = {
        ...study,
        deliveryDate,
        notes,
      };

      if (retargetingCsvColumns) {
        studyPayload.retargetingCsvColumns = retargetingCsvColumns;
      }

      const promises = [
        this.service.update(study.attributionId, studyPayload),
        this.service.updateAttributionStudyCuts(study.attributionId, study.customerId, cuts),
      ]
      return Promise.all(promises).then(() => {
        this.loadStudies();
      });
    },
    onOpenClick(attributionStudy) {
      this.onRowClicked(attributionStudy);
    },
    openEditModal(study) {
      this.service
        .listAttributionStudyCuts(study.attributionId, study.customerId)
        .then((response) => {
          this.cuts = response.data;
          this.selectedAttributionStudy = study;
          this.showEditModal = true;
        });
    },
    isReadyToRun(attributionStudy) {
      return this.isRunAvailableByStatus(attributionStudy);
    },

    isReviewAvailable(study) {
      return (
        study.status === "UNDER_REVIEW" || study.status.indexOf("COMPLETE") > -1
      );
    },

    isRunAvailableByStatus(study) {
      if (
        study.status === "PENDING" ||
        study.status === "COMPLETE" ||
        study.status === "MID_STUDY_COMPLETE"
      ) {
        return true;
      }
      if ((study.status === "ATTENTION_REQUIRED" || study.status === "PROCESSING")) {
        return true;
      }
      
      return false;
    },

    isCompleteAvailableByStatus(study) {
      return (
        (study.status === "UNDER_REVIEW" || study.status === "ATTENTION_REQUIRED") &&
        this.isFinished(study)
      );
    },

    isMidCompleteAvailableByStatus(study) {
      return (
        (study.status === "UNDER_REVIEW" || study.status === "ATTENTION_REQUIRED") &&
        !this.isFinished(study)
      );
    },

    isResetToPendingAvailable(study) {
      return (
        study.status === "UNDER_REVIEW" ||
        study.status === "COMPLETE" ||
        study.status === "MID_STUDY_COMPLETE"
      );
    },

    loadStudies() {
      this.model.loading = true;
      this.service
        .list()
        .then((response) => {
          return Promise.all([response, this.model.areCustomersLoaded, this.service.getAvailableCuts()]);
        })
        .then(([response, _, cutOptions]) => {
          this.cutOptions = cutOptions.data || [];

          this.studies = response.data.sort((a, b) =>
            b.attributionId - a.attributionId 
          );

          this.model.toast.background = "bg-info";
          this.model.toast.message = "Attribution studies loaded successfully.";
          this.setCustomerNamesOnStudies();
          this.model.loading = false;
        })
        .catch((error) => {
          console.log("Error loading attribution studies");
          console.log(error);
          this.model.loading = false;
          this.model.toast.background = "bg-orange";
          this.model.toast.message =
            "There was an error loading attribution studies.";
        });
    },
    onRunStudyClick(study) {
      this.model.toast.message =
      "Kicking off this study...";
      this.service
        .runStudy(study.attributionId, undefined, undefined, this.model.user.displayName)
        .then((response) => {
          this.model.toast.message =
      "Study kicked off successfully!";
          console.log("Running study");
          console.log(response);
          this.loadStudies();
        })
        .catch((message) => {   
          this.model.toast.message =
      "There was an error kicking off this study...";
          console.log("error running study");
          console.log(message);
        });
    },
    selectStatus(newStatus, study) {
      this.selectedStudy = study;
      this.showStatusConfirmModal = true;
      this.onApproveStatusConfirmed = () => {
        if (
          this.selectedStudy.status === "PROCESSING" &&
          newStatus !== "ATTENTION_REQUIRED"
        ) {
          this.model.toast.background = "bg-orange";
          this.model.toast.message =
            "Cannot change status of study that is currently processing.";
          return;
        }
        this.service
          .updateStatus(newStatus, this.selectedStudy.attributionId)
          .then((response) => {
            console.log("Study updated");
            console.log(response);
            this.loadStudies();
          })
          .catch((message) => {
            console.log("error updating study");
            console.log(message);
          });

        this.model.toast.background = "bg-green";
        this.model.toast.message = `Study status changed to ${newStatus}!`;
      };
    },
    setCustomerNamesOnStudies() {
      this.studies.forEach((study) => {
        const foundCustomer = this.model.customers.find(
          (c) => c.customerId === study.customerId
        );
        if (!foundCustomer) return;
        study.customerName = foundCustomer.customerName;
      });
    },
    formatDate(dateValue, withTime = false) {
      if (dateValue.toString().length <= 10) {
        dateValue = +dateValue * 1000;
      }
      const date = new Date(Number(dateValue));
      const formatString = withTime ? "M/D/YY HH:mm:ss" : "MM-DD-YYYY";
      return dayjs(date).format(formatString);
    },

    isFinished(study) {
      const differenceInDays = this.getDaysRemaining(study);
      return differenceInDays < 1;
    },

    isBeforePostStudyEndDate(study) {
      if (!study.postStudyEndDate) {
        return false;
      }
      const now = dayjs();
      const postStudyEndDate = dayjs.unix(study.postStudyEndDate);
      return now.isBefore(postStudyEndDate);
    },

    getDaysRemaining(study) {
      const date1 = dayjs();
      const date2 = dayjs.unix(study.postStudyEndDate);
      return date2.diff(date1, "day");
    },

    getDaysRemainingLabel(study) {
      const differenceInDays = this.getDaysRemaining(study);
      return differenceInDays > 0
        ? `${differenceInDays} Days Remaining`
        : "The study period has ended";
    },

    openCampaignRetargetingModal() {
      this.showCampaignRetargetingModal = true;
    },

    handleCampaignRetargetingSuccess(result) {
      console.log('Campaign retargeting successful:', result);
      // Optionally refresh studies or perform other actions
      this.loadStudies();
    },

    getUniqueCampaignIds(campaigns) {
      if (!campaigns || campaigns.length === 0) {
        return [];
      }
      const uniqueIds = [...new Set(campaigns.map(campaign => campaign.campaignId))];
      return uniqueIds;
    },

    isEligibleForMidpointStudy(study) {
      if (!study.postStudyEndDate) {
        return false;
      }
      const now = dayjs();
      const postStudyEndDate = dayjs.unix(study.postStudyEndDate);
      const daysPastEndDate = now.diff(postStudyEndDate, "day");
      return daysPastEndDate < 8;
    },

    onRunMidpointStudyClick(study) {
      this.selectedStudyForMidpoint = study;
      this.showRunMidpointStudyModal = true;
    },

    handleRunMidpointStudy(study, startDate, endDate) {
      this.model.toast.message = "Kicking off midpoint study...";
      this.service
        .runStudy(study.attributionId, startDate, endDate, this.model.user.displayName)
        .then((response) => {
          this.model.toast.message = "Midpoint study kicked off successfully!";
          console.log("Running midpoint study");
          console.log(response);
          this.loadStudies();
        })
        .catch((message) => {
          this.model.toast.message = "There was an error kicking off this midpoint study...";
          console.log("error running midpoint study");
          console.log(message);
        });
    },
  },
  watch: {
  studies: {
    handler() {
      this.filteredStudies; // Forces recomputation
    },
      deep: true,
    },
    quickFilter() {
      this.filteredStudies; // Forces recomputation when filter changes
  },
}
};
</script>
